import { useState } from 'react';
import { toast } from 'sonner';
import { FaFacebookF, FaInstagram, FaTelegram, FaTiktok, FaWhatsapp } from 'react-icons/fa6';
import { IoChatbubbleEllipses, IoChevronDown, IoShieldCheckmark } from 'react-icons/io5';
import { useGetSocialLinksQuery } from '@/store/api/socialLinksApi';
import LegalModal from '@/components/common/LegalModal';
import { legalPages, legalPagesByLabel, type LegalPageId } from '@/content/legal';
import useContactSupport from '@/hooks/useContactSupport';
import { useInstallApp } from '@/hooks/useInstallApp';

const footerLinks = [
  'Privacy Policy',
  'Terms and Conditions',
  'Rules and Regulations',
  'KYC',
  'Responsible Gaming',
];

// Platform -> icon + the brand's own colour, in the order the row shows
// them. "imo" has no icon in our set (react-icons ships no imo mark) — a
// generic chat-bubble stands in for it. X/Pinterest/YouTube are dropped
// per request — the /social-links API still has rows for them (admin panel
// still lists all 9), they're just not rendered here. Telegram and
// WhatsApp are also left out of this map — they're rendered as fixed,
// non-admin-editable entries (support-contact buttons) at the start of the
// icon row instead, see the JSX below.
const SOCIAL_ICONS: Record<string, { Icon: typeof FaFacebookF; bg: string; label: string }> = {
  facebook: { Icon: FaFacebookF, bg: '#1877F2', label: 'Facebook' },
  instagram: {
    Icon: FaInstagram,
    bg: 'linear-gradient(135deg,#f58529,#dd2a7b,#8134af,#515bd4)',
    label: 'Instagram',
  },
  imo: { Icon: IoChatbubbleEllipses, bg: '#12B7F5', label: 'imo' },
  tiktok: { Icon: FaTiktok, bg: '#000000', label: 'TikTok' },
};

const DESCRIPTION = `In recent years, the online gaming and betting industry has seen exponential growth, attracting players who seek a safe, reliable, and exciting experience. 9WINBET is committed to delivering world-class sportsbook, live casino, slots, and more with top security, fair play, and 24/7 customer support.`;

/**
 * The single site footer — APK download, support contact, legal links,
 * license/social block, all in one dark panel. This used to be split
 * across two visually separate footers (a light one above a dark one);
 * merged into one so the page doesn't look like it has two footers.
 * Social icon URLs come from the admin-editable /social-links API; a
 * platform with no URL set yet still shows its icon (just not clickable)
 * instead of disappearing.
 *
 * License badges use logo art supplied directly by the operator
 * (/public/images/footer). Note for future maintainers: Gaming Curacao's
 * own site states it ceased operations 2024-08-18 and that displaying its
 * seal as still active is "fraudulent and illegal", and Antillephone/
 * Anjouan's real verification seals are domain-locked with restricted
 * usage terms — displaying these badges is the operator's own licensing
 * claim about their current authority, not something this codebase
 * verifies. If that licensing status changes, swap the image files here.
 */
export default function SiteInfoFooter() {
  const { data } = useGetSocialLinksQuery();
  const [expanded, setExpanded] = useState(false);
  const [activePageId, setActivePageId] = useState<LegalPageId | null>(null);
  const links = data?.data ?? [];
  const activePage = legalPages.find((page) => page.id === activePageId) ?? null;
  const { handleWhatsAppSupportClick, handleTelegramSupportClick } = useContactSupport();
  const { isInstalled, handleInstall: handleInstallClick } = useInstallApp();

  return (
    <div className="mt-6 bg-[#0b0b0b] px-4 py-6 text-white">
      {/* Install as app (PWA) */}
      {!isInstalled && (
        <div className="flex flex-col items-center pb-6 text-center">
          <button type="button" onClick={handleInstallClick}>
            <img src="/images/btn-android-dl.png" alt="Install App" className="h-auto w-[9.375rem]" />
          </button>
          <span className="mt-1 text-sm font-semibold text-white/50">
            Get the 9WINBET app — free, fast and full-screen
          </span>
        </div>
      )}

      {/* Legal links */}
      <ul className="flex flex-wrap items-center justify-center pb-6">
        {footerLinks.map((item, index) => {
          const page = legalPagesByLabel[item];
          if (!page) return null;

          return (
            <li key={item} className="inline-flex items-center pb-2">
              <button
                type="button"
                onClick={() => setActivePageId(page.id)}
                className="px-2 text-[0.813rem] leading-none text-white/70"
              >
                {item}
              </button>
              {index !== footerLinks.length - 1 && <span className="text-white/25">|</span>}
            </li>
          );
        })}
      </ul>

      <Section title="Gaming License">
        <div className="flex flex-wrap items-center gap-5">
          <LicenseBadge label="Gaming Curacao" logo="/images/footer/gaming-curacao.webp" />
          <LicenseBadge
            label="Anjouan eGaming"
            sublabel="Antillephone License"
            logo="/images/footer/anjouan-egaming.webp"
          />
          <LicenseBadge label="BMM Testlabs" logo="/images/footer/bmm-testlabs.png" logoOnWhite />
        </div>
      </Section>

      <Section title="Responsible Gaming">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center rounded-lg border border-white/15 bg-white/5 px-3 py-2">
            <img src="/images/footer/rg-gamcare.webp" alt="GamCare" className="h-7 w-auto" />
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-white/85">
            <img src="/images/footer/rg-18plus.webp" alt="18+" className="h-7 w-auto" />
            <span className="text-[0.75rem] font-semibold uppercase">Age restricted</span>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-white/85">
            <img src="/images/footer/rg-stop.webp" alt="" className="h-7 w-auto" />
            <span className="text-[0.75rem] font-semibold uppercase">Play Responsibly</span>
          </div>
        </div>
      </Section>

      {/* One unified icon row — support contact (WhatsApp/Telegram, fixed
          first) plus the admin-editable social links. These used to be two
          separate groups in different parts of the footer (support icons
          up near the APK download, social icons down here) which read as
          duplicated/scattered; now everything icon-shaped lives in this
          one row. A platform with no URL yet still shows its icon, just
          isn't clickable. */}
      <div className="flex flex-wrap justify-center gap-3 py-6">
        <button
          type="button"
          onClick={handleWhatsAppSupportClick}
          aria-label="Contact support on WhatsApp"
        >
          <IconWithLabel Icon={FaWhatsapp} bg="#25D366" label="WhatsApp" />
        </button>
        <button
          type="button"
          onClick={handleTelegramSupportClick}
          aria-label="Contact support on Telegram"
        >
          <IconWithLabel Icon={FaTelegram} bg="#26A5E4" label="Telegram" />
        </button>

        {links.map(({ platform, url }) => {
          const entry = SOCIAL_ICONS[platform];
          if (!entry) return null;
          const content = <IconWithLabel {...entry} />;
          return url ? (
            <a key={platform} href={url} target="_blank" rel="noreferrer" aria-label={platform}>
              {content}
            </a>
          ) : (
            <span key={platform} aria-hidden>
              {content}
            </span>
          );
        })}
      </div>

      <div className="border-t border-white/10 pt-5 text-center">
        <h3 className="text-[0.95rem] font-bold">
          <span className="text-[#e8b923]">9WINBET</span> — Leading Online Gaming and Betting
          Platform
        </h3>

        <p
          className={`mx-auto mt-2 max-w-[26rem] text-[0.8rem] leading-relaxed text-white/60 ${
            expanded ? '' : 'line-clamp-3'
          }`}
        >
          {DESCRIPTION}
        </p>

        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mx-auto mt-3 flex items-center gap-1 rounded border border-white/25 px-4 py-1.5 text-[0.8rem] font-semibold"
        >
          {expanded ? 'Show less' : 'Show more'}
          <IoChevronDown className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>

        <div className="mt-4 flex items-center justify-center gap-1 text-[1.4rem] font-black italic">
          <span className="text-[#e8b923]">9</span>
          <IoShieldCheckmark className="text-[#e8b923]" />
        </div>
      </div>

      <LegalModal
        isOpen={activePage !== null}
        title={activePage?.title ?? ''}
        Content={activePage?.Content}
        onClose={() => setActivePageId(null)}
      />
    </div>
  );
}

// A single circular icon + its name underneath — the shared building block
// for the unified support/social icon row.
function IconWithLabel({
  Icon,
  bg,
  label,
}: {
  Icon: typeof FaWhatsapp;
  bg: string;
  label: string;
}) {
  return (
    <span className="flex flex-col items-center gap-1.5">
      <span
        className="flex size-11 items-center justify-center rounded-full text-lg text-white ring-2 ring-[#e8b923] transition-transform hover:scale-105"
        style={{ background: bg }}
      >
        <Icon />
      </span>
      <span className="text-[0.68rem] font-semibold text-white/70">{label}</span>
    </span>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-white/10 py-4 first:pt-0">
      <h4 className="mb-3 text-[0.95rem] font-bold text-[#e8b923]">{title}</h4>
      {children}
    </div>
  );
}

// Same bordered-pill treatment for all three, so they read as one matched
// set of badges — a real logo (when we have one) sits in this slot; the
// logo art already has the brand name baked into it, so no separate name
// text is repeated next to it (that read as duplicated — same name twice).
// `sublabel` is still shown when given, since it's real info the logo
// itself doesn't say (e.g. "Antillephone License" isn't printed anywhere
// on the Anjouan eGaming logo). Without a logo, a generic shield + the
// name is the fallback, since then there's no baked-in name to rely on.
// Most of the supplied logo art is chrome/white text meant for a dark
// background (it disappears on white), so the backing chip is opt-in —
// only BMM Testlabs' logo (dark text) needs the white square behind it.
function LicenseBadge({
  label,
  sublabel,
  logo,
  logoOnWhite,
}: {
  label: string;
  sublabel?: string;
  logo?: string;
  logoOnWhite?: boolean;
}) {
  if (logo) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-2">
        <span
          className={`flex h-11 w-24 shrink-0 items-center justify-center ${
            logoOnWhite ? 'rounded bg-white p-1' : ''
          }`}
        >
          <img src={logo} alt={label} className="max-h-full max-w-full object-contain" />
        </span>
        {sublabel && (
          <p className="text-[0.62rem] uppercase leading-tight tracking-wide text-white/45">
            {sublabel}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-2">
      <IoShieldCheckmark className="text-[1.6rem] shrink-0 text-[#e8b923]" />
      <div className="leading-tight">
        {sublabel && (
          <p className="text-[0.62rem] uppercase tracking-wide text-white/45">{sublabel}</p>
        )}
        <p className="text-[0.8rem] font-semibold text-white/85">{label}</p>
      </div>
    </div>
  );
}
